/* ==========================================================================
   BC Cart FBT — carousel controls and add-to-cart for the cart drawer.

   The node itself is server-rendered hidden by snippets/bc-cart-fbt.liquid,
   inside the drawer (snippets/header-cart-drawer.liquid). This file unhides
   it on DOMContentLoaded and drives it from then on.

   Handlers are delegated on document rather than bound per element, so the
   arrows can be built and rebuilt — every re-render swaps the contents —
   without re-binding anything.

   THE CARD LIST RECOMPUTES ON EVERY CART CHANGE. It did not originally —
   it was computed in Liquid once per page load, so a drawer could show
   accessories for a basket the shopper no longer had. PR #315 added the
   re-render: bc:cart:updated (from bc-cart-drawer.js) triggers a debounced
   fetch of /?section_id=bc-cart-fbt and the result is swapped into this node.

   The cards also carry discounted prices, which raises the stakes on that
   re-render: a stale recommendation is untidy, a stale PRICE is a number the
   shopper will not be charged. stripDiscountDisplay below handles the one
   path where the component cannot refresh itself.

   An add from a card dispatches cart:build, which rebuilds the drawer's rows
   and totals around this component.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '[data-bc-cart-fbt]';
  var TRACK = '[data-bc-cart-fbt-track]';
  var CARD = '[data-bc-cart-fbt-card]';
  var ADD = '[data-bc-cart-fbt-add]';
  var CONTROLS_CLASS = 'bc-cart-fbt__controls';
  var CARDS_PER_CLICK = 3;

  /* Under the market's route root, never a bare /cart path — see cartRoute
     in bc-cart-item.js. The section fetch too: rendered from "/" on /fr-fr/
     it would price the cards for the wrong market. */
  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + path;
  }

  function scrollAmount(track) {
    var card = track.querySelector(CARD);
    if (!card) return track.clientWidth;

    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;

    return (card.offsetWidth + gap) * CARDS_PER_CLICK;
  }

  function syncArrows(track, prev, next) {
    var maxScroll = track.scrollWidth - track.clientWidth;
    var current = Math.round(track.scrollLeft);
    var tolerance = 5;

    prev.classList.toggle('is-disabled', current <= tolerance);
    prev.setAttribute('aria-disabled', current <= tolerance ? 'true' : 'false');

    next.classList.toggle('is-disabled', current >= maxScroll - tolerance);
    next.setAttribute('aria-disabled', current >= maxScroll - tolerance ? 'true' : 'false');
  }

  function buildArrows(root) {
    var track = root.querySelector(TRACK);
    if (!track) return;

    var viewport = track.parentNode;
    var controls = viewport.querySelector('.' + CONTROLS_CLASS);

    if (!controls) {
      /* The labels come from the locale file, stamped on the root by
         snippets/bc-cart-fbt.liquid, so Langify and the language editor both
         see them. The English fallback is identical, for a root rendered
         before the attributes existed. */
      var labelLeft = root.getAttribute('data-t-scroll-left') || 'Scroll recommendations left';
      var labelRight = root.getAttribute('data-t-scroll-right') || 'Scroll recommendations right';

      controls = document.createElement('div');
      controls.className = CONTROLS_CLASS;

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'bc-cart-fbt__arrow bc-cart-fbt__arrow--prev';
      prev.setAttribute('aria-label', labelLeft);
      prev.innerHTML = '&#8249;';

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'bc-cart-fbt__arrow bc-cart-fbt__arrow--next';
      next.setAttribute('aria-label', labelRight);
      next.innerHTML = '&#8250;';

      controls.appendChild(prev);
      controls.appendChild(next);
      viewport.insertBefore(controls, track);

      prev.addEventListener('click', function () {
        if (prev.classList.contains('is-disabled')) return;
        track.scrollBy({ left: -scrollAmount(track), behavior: 'smooth' });
      });

      next.addEventListener('click', function () {
        if (next.classList.contains('is-disabled')) return;
        track.scrollBy({ left: scrollAmount(track), behavior: 'smooth' });
      });

      /* Bound to the track, so it dies with the track when a re-render
         replaces it. Safe to bind here; the window-level handler is not —
         see refreshArrows below. */
      track.addEventListener('scroll', function () {
        syncArrows(track, prev, next);
      });
    }

    var prevButton = controls.querySelector('.bc-cart-fbt__arrow--prev');
    var nextButton = controls.querySelector('.bc-cart-fbt__arrow--next');

    setArrowVisibility(track, controls);
    syncArrows(track, prevButton, nextButton);
  }

  /* Nothing to scroll means no arrows — a fallback list can be shorter than the
     viewport.

     ⚠ BUT A ZERO-WIDTH TRACK IS NOT "NOTHING TO SCROLL", IT IS NO ANSWER AT
     ALL, and reading it as one is what made the arrows disappear.

     The drawer is `display: none` until it opens — theme.css:6870 for
     .site-header__drawer, overridden only for .is-active. An ancestor at
     display:none takes its whole subtree with it, so every descendant measures
     0. The old line then read `0 <= 0 + 5` as true and hid the controls.

     That fires on any measurement taken with the drawer shut, and since stage
     5 there is one: activate() runs on DOMContentLoaded. Nothing re-measured
     on open, so the arrows stayed hidden for the life of the page — unless a
     cart change re-rendered the component while the drawer was open, which is
     exactly what every device test happened to do, which is why this survived
     four of them.

     Undecidable is now left alone: the controls keep whatever state they had
     rather than being hidden on a measurement that means nothing. */
  function setArrowVisibility(track, controls) {
    if (!track || !controls) return;
    if (!track.clientWidth) return;
    controls.hidden = track.scrollWidth <= track.clientWidth + 5;
  }

  /* Re-queries the live track and controls on every call instead of closing
     over them.

     THIS IS WHY IT IS NOT BOUND INSIDE buildArrows. A re-render replaces the
     component's innerHTML, which destroys the controls, so buildArrows' `if
     (!controls)` branch re-enters and rebuilds them. Anything bound to
     `window` in there is bound again on every cart change and never removed,
     each copy closing over a track and controls that are now detached —
     reading scrollWidth off a dead node and writing `hidden` to a control
     nobody can see. One listener per cart change, accumulating for the life
     of the page.

     Bound once at module scope, reading the DOM fresh each run, it is
     indifferent to how many times the contents are swapped. Same reasoning as
     the drag handlers, which are delegated on document for exactly this. */
  function refreshArrows() {
    var root = document.querySelector(ROOT);
    if (!root) return;

    var track = root.querySelector(TRACK);
    var controls = root.querySelector('.' + CONTROLS_CLASS);
    if (!track || !controls) return;

    var prev = controls.querySelector('.bc-cart-fbt__arrow--prev');
    var next = controls.querySelector('.bc-cart-fbt__arrow--next');
    if (!prev || !next) return;

    setArrowVisibility(track, controls);
    syncArrows(track, prev, next);
  }

  window.addEventListener('resize', refreshArrows);

  /* THE MEASUREMENT HAS TO HAPPEN WHEN THE DRAWER IS ACTUALLY OPEN, and until
     now nothing made it. theme.js dispatches CartDrawer:open from
     HeaderCart.open() (theme.js:5161) — the same event bc-cart-drawer.js uses
     to harvest the wallet buttons, and for the same reason: both need the
     panel laid out before they can measure anything.

     rAF because the class that un-hides the panel is applied in the same tick
     as the event; reading widths before the next frame reads the old layout.

     The guard above means it can only ever reveal arrows that should be
     showing, never hide any. */
  document.addEventListener('CartDrawer:open', function () {
    window.requestAnimationFrame(refreshArrows);
  });

  /* Unhides the component and builds its arrows. Safe to call repeatedly:
     on DOMContentLoaded and after every re-render. */
  function activate() {
    var root = document.querySelector(ROOT);
    if (!root) return;

    /* The container is now emitted for an empty cart too, so it can be
       populated by a swap without a page load. Unhiding it unconditionally
       would put an empty bordered box over an empty basket. Presence of a
       card is the test for "there is something to show". */
    if (!root.querySelector(CARD)) {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    buildArrows(root);
  }

  /* ---- Click-and-drag ----------------------------------------------------
     The track is a native overflow-x scroller, so TOUCH SWIPE ALREADY WORKS
     and is deliberately left alone — touch-action is auto on the track and
     every ancestor, and nothing calls preventDefault on it. Handling touch
     here would replace a compositor-driven gesture that already disambiguates
     horizontal swipe from vertical page scroll with a JS approximation that
     does it worse. So the drag path is gated to non-touch pointers; touch
     never enters it, which is what guarantees vertical scrolling is untouched.

     Delegated on document, not bound to the track, so it survives the
     re-render on every cart change without needing to re-attach. */
  var DRAG_THRESHOLD = 6;
  var drag = null;
  var suppressClick = false;

  document.addEventListener('pointerdown', function (event) {
    /* Any pointerdown starts a fresh gesture, so a suppression left over from
       a drag that ended outside the component can never eat a later click. */
    suppressClick = false;

    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    var track = event.target.closest ? event.target.closest(TRACK) : null;
    if (!track) return;
    if (track.scrollWidth <= track.clientWidth) return;

    drag = {
      track: track,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      pointerId: event.pointerId,
      moved: false
    };
  });

  document.addEventListener('pointermove', function (event) {
    if (!drag || event.pointerId !== drag.pointerId) return;

    var dx = event.clientX - drag.startX;

    if (!drag.moved) {
      /* 6px, just above the ~5px browsers use themselves to tell a click from
         a drag. Below this nothing moves and the click reaches the button, so
         a normal press on "Add to cart" still adds. */
      if (Math.abs(dx) < DRAG_THRESHOLD) return;

      drag.moved = true;
      drag.track.classList.add('is-dragging');

      /* Both of these fight a drag and both must go for its duration:
         with scroll-behavior:smooth a direct scrollLeft assignment is a
         NO-OP — measured on the dev theme, the value stayed 0 for 1.3s —
         and scroll-snap-type:mandatory drags the track back mid-gesture.
         Restoring the snap on release is also what settles the track onto
         the nearest card, which is why no momentum is simulated. */
      drag.track.style.scrollBehavior = 'auto';
      drag.track.style.scrollSnapType = 'none';

      try { drag.track.setPointerCapture(drag.pointerId); } catch (e) {}
    }

    drag.track.scrollLeft = drag.startScroll - dx;
    event.preventDefault();
  });

  function endDrag(event) {
    if (!drag || (event && event.pointerId !== drag.pointerId)) return;

    var finished = drag;
    drag = null;

    if (!finished.moved) return;

    finished.track.classList.remove('is-dragging');
    finished.track.style.scrollBehavior = '';
    finished.track.style.scrollSnapType = '';

    try { finished.track.releasePointerCapture(finished.pointerId); } catch (e) {}

    /* A real drag is followed by a click on whatever was under the cursor.
       Without this, letting go over a card would add it to the basket. */
    suppressClick = true;
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* Capture phase, so this runs before the delegated add-to-cart handler
     below, which is on the bubble phase. */
  document.addEventListener(
    'click',
    function (event) {
      if (!suppressClick) return;
      suppressClick = false;

      if (!event.target.closest || !event.target.closest(ROOT)) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  /* Card images and titles are links, and a mouse drag across a link starts
     the browser's own drag-and-drop, which cancels the pointer gesture and
     leaves a ghost image following the cursor. */
  document.addEventListener('dragstart', function (event) {
    if (event.target.closest && event.target.closest(TRACK)) event.preventDefault();
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest(ADD) : null;
    if (!button || button.disabled) return;

    event.preventDefault();

    var variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId) return;

    var label = button.querySelector('[data-bc-cart-fbt-add-label]');
    var addedText = button.getAttribute('data-label-added') || 'Added';

    button.disabled = true;

    fetch(cartRoute('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) {
          /* STAGE 6 — carry Shopify's own words out of the body rather than
             throwing them away. A 422 here is a real refusal, usually the last
             one in stock already being in the basket, and "You can only add 2
             of this item to your cart" is a sentence only Shopify can write.
             The parse is guarded: a body we cannot read leaves description
             undefined and the caller falls back to the locale string. */
          return response.json().catch(function () { return null; }).then(function (body) {
            var refusal = new Error('cart add refused');
            refusal.description = body && body.description;
            throw refusal;
          });
        }
        return response.json();
      })
      .then(function () {
        button.classList.add('is-added');
        if (label) label.textContent = addedText;

        /* Nothing in the theme listens for cart:refresh — bc-variant-bundle.js
           dispatches it and no handler exists — but other apps may, and it is
           the theme's existing convention. */
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        /* The drawer is already open, so there is nothing to show — what is
           needed is a rebuild. /cart/add.js does not touch [data-products],
           so without this the shopper adds an accessory and the rows, the
           count and the totals beside it all keep their old values.

           cart:build -> theme.js CartForm.buildCart() -> one /cart?view=ajax
           fetch -> [data-products] replaced -> bc-cart-drawer.js's observer
           -> bc:cart:updated -> this component re-renders. One chain, one
           cart fetch, no reload.

           It cannot loop: this component's own swap happens inside its own
           node, and the observer that starts the chain watches
           [data-products], which is a different subtree. */
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function (error) {
        /* Re-enable so the shopper can retry rather than being left with a
           dead button. */
        button.disabled = false;
        button.classList.remove('is-added');
        if (label) label.textContent = button.getAttribute('data-label-add') || 'Add to cart';

        /* STAGE 6 — THIS IS THE CASE THE DRAWER MESSAGE EXISTS FOR. The drawer
           is open, the card is in it, and until now a refused add reset the
           button and said nothing, which reads exactly like a broken button. */
        if (window.bcCartDrawer) {
          window.bcCartDrawer.showError(
            (error && error.description) ||
            (window.theme && theme.strings && theme.strings.cartUpdateError) ||
            'Sorry, we could not update your basket. Please try again.'
          );
        }

        console.error('BC Cart FBT: add to cart failed', error);
      });
  });

  /* ======================================================================
     RE-RENDER ON CART CHANGE

     The card list is computed in Liquid against cart.items as they stood
     when the page HTML was generated. Before this, nothing recomputed it:
     any cart change left the component stale, so a basket holding only a
     stove could keep showing tent accessories from an earlier page load.
     This re-fetches the component and swaps its contents in place.

     SWAP innerHTML, NEVER REPLACE THE NODE. Every handler in this file is
     delegated on document and the node keeps its place in the drawer, so
     nothing needs re-binding or re-positioning by construction.

     TRIGGER. bc:cart:updated, dispatched by bc-cart-drawer.js after every
     cart change once the footer is in step.
     ====================================================================== */
  var SECTION_PATH = '?section_id=bc-cart-fbt';
  var DEBOUNCE_MS = 275;
  var SIG_ATTR = 'data-bc-cart-fbt-signature';

  var debounceTimer = null;
  var inFlight = null;
  var dirty = false;

  function currentSignature() {
    var root = document.querySelector(ROOT);
    return root ? root.getAttribute(SIG_ATTR) : null;
  }

  function applyRerender(html) {
    var root = document.querySelector(ROOT);
    if (!root) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    /* The Section Rendering API wraps output in #shopify-section-…, so take
       the component out of it rather than mounting the wrapper. */
    var fresh = doc.querySelector(ROOT);
    if (!fresh) return;

    var incoming = fresh.getAttribute(SIG_ATTR);
    if (incoming !== null && incoming === root.getAttribute(SIG_ATTR)) return;

    root.innerHTML = fresh.innerHTML;
    if (incoming !== null) root.setAttribute(SIG_ATTR, incoming);

    activate();
  }

  /* Reverts every mounted card to its pre-discount price and drops the
     struck price beside it. data-bc-full-price is written by
     snippets/bc-cart-fbt-card.liquid and is the same hand-built string the
     server would render, so this cannot reintroduce the money-format
     rounding it exists to avoid. Compare-at is left alone: it is a property
     of the product, not of the discount. Idempotent. */
  function stripDiscountDisplay() {
    var root = document.querySelector(ROOT);
    if (!root) return;
    var cards = root.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) {
      var was = cards[i].querySelector('.bc-cart-fbt__was');
      if (!was) continue;
      var price = cards[i].querySelector('.bc-cart-fbt__price');
      var full = price && price.getAttribute('data-bc-full-price');
      if (price && full) price.textContent = full;
      was.parentNode.removeChild(was);
    }
  }

  function fetchAndSwap() {
    /* One request at a time. A second trigger mid-flight sets dirty and is
       serviced once this settles, rather than racing it — empty-then-add
       fires 20 events and add-from-PDP 9, so without this a single shopper
       action would open a fistful of parallel requests. */
    if (inFlight) {
      dirty = true;
      return;
    }

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight = controller || true;

    fetch(cartRoute(SECTION_PATH), {
      signal: controller ? controller.signal : undefined,
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('section fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        applyRerender(html);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        /* Leaving the previous cards is right for the RECOMMENDATIONS — a
           stale list beats an empty drawer. It is wrong for the PRICES: the
           cart has changed, and the discount shown against these cards may
           no longer be one the shopper earns. So the cards stay and the
           discount display comes off, back to plain pre-discount prices.
           Understating the saving is recoverable; quoting a price checkout
           will not honour is not. */
        stripDiscountDisplay();
        console.error('BC Cart FBT: re-render failed, discount display removed', error);
      })
      .then(function () {
        inFlight = null;
        if (dirty) {
          dirty = false;
          scheduleRerender();
        }
      });
  }

  function scheduleRerender() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(fetchAndSwap, DEBOUNCE_MS);
  }

  /* Nothing else unhides the component, so without this it renders and
     stays hidden. No guard is needed for an empty basket: activate() refuses
     to unhide a component with no cards, and .bc-drawer.is-empty hides the
     whole form around it in any case. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', activate);
  } else {
    activate();
  }

  document.addEventListener('bc:cart:updated', scheduleRerender);
})();
